// BoardActivityContext — App-level, SELF-CONTAINED source of truth for "is the Auto-Insights
// board doing work right now" (generating / streaming visuals), PER PROJECT.
//
// WHY THIS OWNS ITS OWN POLL (roadmap #83, after several failed attempts):
//   The board renders in AutoDeck, which lives inside Analyze OR Dashboard. App.jsx renders one
//   section at a time, so switching sections UNMOUNTS AutoDeck; switching browser windows/tabs
//   THROTTLES its setTimeout poll. Every prior fix derived the spinner from the mounted AutoDeck's
//   board state, so it broke on remount load-windows, section switches, and tab throttling.
//
//   Root fix: this provider is mounted ABOVE the sections (never unmounts) and runs its OWN poll
//   of GET /ai/board. A button click calls beginWork(projectId) → the provider polls every few
//   seconds, setting working[projectId] = (status==='generating' OR any visual still 'pending'),
//   and stops only when the board is genuinely done. It also re-kicks immediately when the tab
//   becomes visible again. The Analyze "Regenerate board" and Dashboard "Refresh" buttons BOTH
//   read working[projectId] and BOTH call beginWork — so they are always in sync and the spinner
//   is completely independent of which page is mounted or whether the tab was backgrounded.
import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { getFreshToken } from "../api";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
const POLL_MS = 3000;      // steady poll while generating
const RETRY_MS = 4000;     // after a transient fetch error
const START_MS = 1200;     // faster poll during the "grace" window (waiting for a click's regen to start)
const GRACE_MS = 12000;    // keep the spinner on this long after a click even if the board still reads
//                            "done" — bridges the race between the click and the new generation being
//                            persisted server-side, so the spinner can't die just as generation begins.

const BoardActivityContext = createContext(null);

function boardIsWorking(data) {
  const b = data && data.board;
  if (!data || !data.enabled || !b) return false;
  if (b.status === "generating") return true;
  return Array.isArray(b.visuals) && b.visuals.some((v) => v && v.status === "pending");
}

export function BoardActivityProvider({ children }) {
  const [working, setWorking] = useState({});   // { [projectId]: boolean }
  const workingRef = useRef({});                 // mirror for callbacks (no re-render dependency)
  const meta = useRef({});                       // { [projectId]: { startedAt, sawGenerating } }
  const timers = useRef({});                     // { [projectId]: timeout id } — at most one poll in flight per project
  const mounted = useRef(true);

  const _apply = useCallback((projectId, val) => {
    workingRef.current = { ...workingRef.current, [projectId]: !!val };
    setWorking((prev) => (!!prev[projectId] === !!val ? prev : { ...prev, [projectId]: !!val }));
  }, []);

  const _poll = useCallback(async (projectId) => {
    if (!projectId || !mounted.current) return;
    timers.current[projectId] = null;
    try {
      const token = await getFreshToken();
      const res = await fetch(`${API_URL}/ai/board`, {
        headers: { Authorization: `Bearer ${token}`, "X-Project-Id": projectId },
      });
      if (res.ok) {
        const isWorking = boardIsWorking(await res.json());
        if (!mounted.current) return;
        const m = meta.current[projectId] || { startedAt: 0, sawGenerating: false };
        if (isWorking) {
          // Confirmed the board is producing — normal steady poll until done.
          m.sawGenerating = true;
          meta.current[projectId] = m;
          _apply(projectId, true);
          timers.current[projectId] = setTimeout(() => _poll(projectId), POLL_MS);
          return;
        }
        // Board reads "done". If we haven't YET seen it generate and we're still inside the grace
        // window after the click, this is almost certainly the pre-generation race (we read the
        // previous board before the new generation persisted) — keep the spinner on and poll fast.
        if (!m.sawGenerating && Date.now() - (m.startedAt || 0) < GRACE_MS) {
          _apply(projectId, true);
          timers.current[projectId] = setTimeout(() => _poll(projectId), START_MS);
          return;
        }
        // Genuinely done (or grace elapsed with nothing generating) → stop.
        _apply(projectId, false);
        return;
      }
      // Non-OK (transient/gateway): assume still working and retry a bit slower.
    } catch {
      // Network blip / token refresh hiccup — keep the spinner up and retry.
    }
    if (mounted.current && workingRef.current[projectId]) {
      timers.current[projectId] = setTimeout(() => _poll(projectId), RETRY_MS);
    }
  }, [_apply]);

  const _ensurePolling = useCallback((projectId) => {
    if (projectId && !timers.current[projectId]) {
      timers.current[projectId] = setTimeout(() => _poll(projectId), 0);
    }
  }, [_poll]);

  // A button click (or AutoDeck observing generation) marks work started + kicks the poll loop.
  // Records a fresh grace window so a click's spinner can't be killed by the pre-generation race.
  const beginWork = useCallback((projectId) => {
    if (!projectId) return;
    meta.current[projectId] = { startedAt: Date.now(), sawGenerating: false };
    _apply(projectId, true);
    _ensurePolling(projectId);
  }, [_apply, _ensurePolling]);

  // Returning to a backgrounded tab: re-kick a poll immediately for any project still marked
  // working (its setTimeout was throttled/frozen while hidden), so the spinner reconciles at once.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      Object.keys(workingRef.current).forEach((pid) => {
        if (workingRef.current[pid]) _ensurePolling(pid);
      });
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [_ensurePolling]);

  useEffect(() => () => {
    mounted.current = false;
    Object.values(timers.current).forEach((t) => t && clearTimeout(t));
  }, []);

  return (
    <BoardActivityContext.Provider value={{ working, beginWork }}>
      {children}
    </BoardActivityContext.Provider>
  );
}

export function useBoardActivity() {
  return useContext(BoardActivityContext) || { working: {}, beginWork: () => {} };
}
